import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zhTW } from "date-fns/locale";
import { useNavigate, useParams } from "react-router-dom";
import { MdError, MdKeyboardArrowLeft, MdOutlineUploadFile } from "react-icons/md";
import { FiTrash2 } from "react-icons/fi";

import Button from "../Utilities/Button";
import Datepicker from "../Utilities/Datepicker/Datepicker";
import TextField from "../Utilities/TextField";
import Toast from "../Utilities/Toast";
import Alert from "../Utilities/Alert";
import RightPanelLayout from "../Layout/RightPanelLayout";
import Switcher from "../Utilities/AlertSwitch/Switcher";

import baseStyles from "../EventManagement/style/EventFormPage.module.scss";
import releaseStyles from "../AnnouncementManagement/style/ReleaseNoteFormPage.module.scss";
import styles from "./style/AiLearningResourceFormPage.module.scss";
import {
  createAiLearningResourceMultipart,
  deleteAiLearningResource,
  fetchAiLearningResourceById,
  updateAiLearningResource,
  updateAiLearningResourceMultipart,
} from "./aiLearningResourcesApi";
import {
  buildAiLearningResourceMultipartPayload,
  buildAiLearningResourcePayload,
  GOOGLE_DRIVE_DOWNLOAD_BASE,
  mapAiLearningResourceDetailToFormValues,
} from "./aiLearningResourcesTransforms";

const nowLocalForInput = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};

const isValidHttpUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return false;
  try {
    const parsed = new URL(raw);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const isValidGoogleFileId = (value) => /^[a-zA-Z0-9_-]{10,}$/.test(String(value || "").trim());

const AiLearningResourceFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const imageInputRef = useRef(null);
  const googleDriveIdInputRef = useRef(null);

  const initialForm = useMemo(
    () => ({
      title: "",
      uploadedAt: `${nowLocalForInput().slice(0, 10)}T00:00`,
      googleDriveFileId: "",
      youtubeUrl: "",
      facebookUrl: "",
      instagramUrl: "",
      display: true,
    }),
    [],
  );

  const {
    watch,
    setValue,
    reset,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: initialForm,
  });

  const form = watch();
  const [detailLoading, setDetailLoading] = useState(mode === "edit");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toolbarSaveWarning, setToolbarSaveWarning] = useState("");
  const [isConfirmLeaveOpen, setIsConfirmLeaveOpen] = useState(false);
  const [toastState, setToastState] = useState({
    show: false,
    title: "",
    subtitle: "",
    error: false,
  });
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState("");

  const showToast = (title, error = false) => {
    setToastState({ show: true, title, subtitle: "", error });
    window.setTimeout(() => {
      setToastState((s) => ({ ...s, show: false }));
    }, 3200);
  };

  useEffect(() => {
    return () => {
      if (coverImagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(coverImagePreview);
      }
    };
  }, [coverImagePreview]);

  useEffect(() => {
    if (mode !== "edit" || !id) {
      setDetailLoading(false);
      reset(initialForm);
      clearErrors();
      return;
    }

    let cancelled = false;
    setDetailLoading(true);
    fetchAiLearningResourceById(id)
      .then((data) => {
        if (cancelled) return;
        const mapped = mapAiLearningResourceDetailToFormValues(data);
        reset(mapped);
        setCoverImageFile(null);
        setCoverImagePreview(mapped.coverImageUrl || "");
        clearErrors();
      })
      .catch(() => {
        if (cancelled) return;
        navigate("/dashboard/tdp/ai-learning-resources");
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mode, id, reset, clearErrors, navigate, initialForm]);

  useEffect(() => {
    if (mode === "new") {
      reset(initialForm);
      clearErrors();
      setToolbarSaveWarning("");
      setCoverImageFile(null);
      setCoverImagePreview("");
    }
  }, [mode, initialForm, reset, clearErrors]);

  useEffect(() => {
    if (!isDirty) return undefined;

    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      setIsConfirmLeaveOpen(true);
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isDirty]);

  const setField = (key, value) => {
    setValue(key, value, { shouldDirty: true });
    if (String(value || "").trim()) {
      clearErrors(key);
    }
    if (toolbarSaveWarning) {
      setToolbarSaveWarning("");
    }
  };

  const uploadedDatePart =
    form.uploadedAt && form.uploadedAt.length >= 10
      ? form.uploadedAt.slice(0, 10)
      : "";

  const uploadedDateForPicker = useMemo(() => {
    if (!uploadedDatePart) return "";
    const d = new Date(`${uploadedDatePart}T12:00:00`);
    return Number.isNaN(d.getTime()) ? "" : d;
  }, [uploadedDatePart]);

  const handleUploadedDatePick = (date) => {
    if (!date) {
      setField("uploadedAt", "");
      return;
    }
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    setField("uploadedAt", `${yyyy}-${mm}-${dd}T00:00`);
  };

  const handleBack = () => {
    if (!isDirty) {
      navigate("/dashboard/tdp/ai-learning-resources");
      return;
    }
    setIsConfirmLeaveOpen(true);
  };

  const handleLeaveConfirm = () => {
    setIsConfirmLeaveOpen(false);
    navigate("/dashboard/tdp/ai-learning-resources");
  };

  const handleSave = async () => {
    if (isSubmitting) return;
    setToolbarSaveWarning("");
    clearErrors();

    const requiredFields = [
      { key: "title", value: form.title, message: "請輸入標題" },
      {
        key: "googleDriveFileId",
        value: form.googleDriveFileId,
        message: "請輸入 Google Drive 檔案 ID",
      },
    ];

    const missing = requiredFields.find(
      (field) => !String(field.value || "").trim(),
    );
    if (missing) {
      setError(missing.key, { type: "manual", message: "此欄位為必填" });
      setToolbarSaveWarning(missing.message);
      return;
    }

    const invalidUrlField = [
      {
        key: "youtubeUrl",
        value: form.youtubeUrl,
        message: "YouTube 連結格式錯誤",
      },
      {
        key: "facebookUrl",
        value: form.facebookUrl,
        message: "Facebook 連結格式錯誤",
      },
      {
        key: "instagramUrl",
        value: form.instagramUrl,
        message: "Instagram 連結格式錯誤",
      },
    ].find((field) => {
      const raw = String(field.value || "").trim();
      if (!raw) return false;
      return !isValidHttpUrl(raw);
    });

    const googleFileIdRaw = String(form.googleDriveFileId || "").trim();
    if (!isValidGoogleFileId(googleFileIdRaw)) {
      setError("googleDriveFileId", {
        type: "manual",
        message: "Google Drive 檔案 ID 格式錯誤",
      });
      setToolbarSaveWarning("Google Drive 檔案 ID 格式錯誤");
      return;
    }

    if (invalidUrlField) {
      setError(invalidUrlField.key, {
        type: "manual",
        message: invalidUrlField.message,
      });
      setToolbarSaveWarning(invalidUrlField.message);
      return;
    }

    if (mode === "new" && !(coverImageFile instanceof File)) {
      setError("coverImage", { type: "manual", message: "此欄位為必填" });
      setToolbarSaveWarning("請上傳封面圖");
      return;
    }

    if (mode === "edit" && !coverImagePreview && !(coverImageFile instanceof File)) {
      setError("coverImage", { type: "manual", message: "此欄位為必填" });
      setToolbarSaveWarning("請上傳封面圖");
      return;
    }

    setIsSubmitting(true);
    try {
      if (mode === "edit" && id) {
        if (coverImageFile instanceof File) {
          const multipart = buildAiLearningResourceMultipartPayload({
            title: form.title,
            uploadedAtLocal: form.uploadedAt,
            googleDriveFileId: form.googleDriveFileId,
            youtubeUrl: form.youtubeUrl,
            facebookUrl: form.facebookUrl,
            instagramUrl: form.instagramUrl,
            display: form.display,
            coverImageFile,
          });
          await updateAiLearningResourceMultipart(id, multipart);
        } else {
          const payload = buildAiLearningResourcePayload({
            title: form.title,
            uploadedAtLocal: form.uploadedAt,
            googleDriveFileId: form.googleDriveFileId,
            youtubeUrl: form.youtubeUrl,
            facebookUrl: form.facebookUrl,
            instagramUrl: form.instagramUrl,
            display: form.display,
          });
          await updateAiLearningResource(id, payload);
        }
        showToast("已更新教學資源卡片");
      } else {
        const multipart = buildAiLearningResourceMultipartPayload({
          title: form.title,
          uploadedAtLocal: form.uploadedAt,
          googleDriveFileId: form.googleDriveFileId,
          youtubeUrl: form.youtubeUrl,
          facebookUrl: form.facebookUrl,
          instagramUrl: form.instagramUrl,
          display: form.display,
          coverImageFile,
        });
        await createAiLearningResourceMultipart(multipart);
        showToast("已建立教學資源卡片");
      }
      setTimeout(() => {
        navigate("/dashboard/tdp/ai-learning-resources");
      }, 300);
    } catch {
      showToast("儲存失敗請重試", true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (mode !== "edit" || !id || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await deleteAiLearningResource(id);
      setIsDeleteOpen(false);
      showToast("已刪除教學資源卡片");
      setTimeout(() => {
        navigate("/dashboard/tdp/ai-learning-resources");
      }, 300);
    } catch {
      showToast("刪除失敗請重試", true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTestDownloadLink = () => {
    const fileId = String(form.googleDriveFileId || "").trim();
    if (!isValidGoogleFileId(fileId)) {
      showToast("請先輸入正確的 Google Drive 檔案 ID", true);
      return;
    }
    window.open(`${GOOGLE_DRIVE_DOWNLOAD_BASE}${fileId}`, "_blank", "noopener,noreferrer");
  };

  if (detailLoading) {
    return (
      <RightPanelLayout title='AI 智庫'>
        <div className={releaseStyles.previewSaveHint}>載入中…</div>
      </RightPanelLayout>
    );
  }

  return (
    <RightPanelLayout title='AI 智庫'>
      <form
        className={baseStyles.form}
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        <div className={baseStyles.topToolbarHeader}>
          <button
            type='button'
            className={baseStyles.backButton}
            onClick={handleBack}
          >
            <MdKeyboardArrowLeft size={32} />
            返回
          </button>

          <div className={baseStyles.rightActions}>
            {toolbarSaveWarning ? (
              <span className={baseStyles.toolbarWarningText}>
                <MdError size={22} aria-hidden />
                {toolbarSaveWarning}
              </span>
            ) : null}

            <button
              type='button'
              className={styles.testDownloadButton}
              onClick={handleTestDownloadLink}
              disabled={
                isSubmitting ||
                !isValidGoogleFileId(String(form.googleDriveFileId || "").trim())
              }
            >
              測試下載連結
            </button>

            <Button
              type='primary'
              title={isSubmitting ? "儲存中…" : "儲存"}
              disabled={isSubmitting}
            />

            {mode === "edit" ? (
              <button
                type='button'
                className={baseStyles.deleteButton}
                onClick={() => setIsDeleteOpen(true)}
                aria-label='刪除教學資源'
              >
                <FiTrash2 size={24} aria-hidden />
                刪除
              </button>
            ) : null}
          </div>
        </div>

        <div className={releaseStyles.whiteCard}>
          <div className={baseStyles.columnStack}>
            <div className={baseStyles.infoRow}>
              <div className={`${baseStyles.infoRowLabel} ${styles.alignedLabel}`}>
                標題<span className={baseStyles.requiredPrimary}>*</span>
              </div>
              <div className={baseStyles.infoRowBody}>
                <TextField
                  placeholder='請輸入'
                  width='100%'
                  fullWidth
                  value={form.title}
                  error={Boolean(errors.title)}
                  onChange={(v) => setField("title", v)}
                />
              </div>
            </div>

            <div className={baseStyles.infoRow}>
              <div className={`${baseStyles.infoRowLabel} ${styles.alignedLabel}`}>
                上架時間
              </div>
              <div className={baseStyles.infoRowBody}>
                <div className={baseStyles.activityTimeControls}>
                  <div className={baseStyles.activityDatePickerWrap}>
                    <Datepicker
                      selectDate={uploadedDateForPicker}
                      setSelectDate={handleUploadedDatePick}
                      error={Boolean(errors.uploadedAt)}
                      iconType='arrow'
                      placeholderText='請選擇日期'
                      dateFormat='yyyy-MM-dd'
                      locale={zhTW}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={baseStyles.infoRow}>
              <div className={`${baseStyles.infoRowLabel} ${styles.alignedLabel}`}>
                顯示於官網
              </div>
              <div className={baseStyles.infoRowBody}>
                <div className={baseStyles.switchRow}>
                  <Switcher
                    isChecked={Boolean(form.display)}
                    onChange={() => setField("display", !Boolean(form.display))}
                    color={{ on: "#5856D6", off: "#5856D64D" }}
                    width={33}
                    height={20}
                  />
                  <span className={releaseStyles.statusText}>
                    {form.display ? "上架" : "未上架"}
                  </span>
                </div>
              </div>
            </div>

            <div className={baseStyles.infoRow}>
              <div className={`${baseStyles.infoRowLabel} ${styles.alignedLabel}`}>
                圖片<span className={baseStyles.requiredPrimary}>*</span>
              </div>
              <div className={baseStyles.infoRowBody}>
                <div className={releaseStyles.imageUploadLayout}>
                  <input
                    ref={imageInputRef}
                    type='file'
                    accept='image/png,image/jpeg,image/jpg,image/gif,image/webp'
                    className={releaseStyles.hiddenInput}
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      e.target.value = "";
                      if (!file) return;
                      setCoverImageFile(file);
                      clearErrors("coverImage");
                      if (coverImagePreview.startsWith("blob:")) {
                        URL.revokeObjectURL(coverImagePreview);
                      }
                      setCoverImagePreview(URL.createObjectURL(file));
                    }}
                  />
                  <button
                    type='button'
                    className={releaseStyles.uploadButton}
                    onClick={() => imageInputRef.current?.click()}
                  >
                    <MdOutlineUploadFile size={18} aria-hidden />
                    上傳
                  </button>
                  <div className={releaseStyles.uploadedFileNameArea}>
                    <div className={releaseStyles.uploadedFileNames}>
                      {coverImageFile ? (
                        <span className={releaseStyles.uploadedFileName}>
                          {coverImageFile.name}
                        </span>
                      ) : coverImagePreview ? (
                        <span className={releaseStyles.uploadedFileName}>
                          已上傳封面圖
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
                {coverImagePreview ? (
                  <img
                    src={coverImagePreview}
                    alt='resource-cover-preview'
                    className={styles.coverPreview}
                  />
                ) : null}
              </div>
            </div>

            <div className={baseStyles.infoRow}>
              <div className={`${baseStyles.infoRowLabel} ${styles.alignedLabel} ${styles.googleDriveLabel}`}>
                Google Drive
                <span className={baseStyles.requiredPrimary}>*</span>
              </div>
              <div className={baseStyles.infoRowBody}>
                <div
                  className={`${styles.googleDriveInputWrap} ${
                    errors.googleDriveFileId ? styles.googleDriveInputWrapError : ""
                  }`}
                  onClick={() => googleDriveIdInputRef.current?.focus()}
                >
                  <span className={styles.googleDriveInputPrefix}>
                    {GOOGLE_DRIVE_DOWNLOAD_BASE}
                  </span>
                  <input
                    ref={googleDriveIdInputRef}
                    type='text'
                    value={form.googleDriveFileId}
                    placeholder='請貼上檔案 ID'
                    onChange={(e) => setField("googleDriveFileId", e.target.value)}
                    className={styles.googleDriveIdInput}
                    aria-label='Google Drive 檔案 ID'
                  />
                </div>
                <div className={styles.googleDriveHint}>
                  <span className={styles.googleDriveExample}>
                    藍色部分為ID：
                    https://drive.google.com/file/d/
                    <span className={styles.googleDriveIdHighlight}>
                      1WExMVAxBVI5I5eTbE_Ri1BhoEo4ZJP13
                    </span>
                    /view
                  </span>
                </div>
                {errors.googleDriveFileId ? (
                  <span className={styles.errorText}>
                    {errors.googleDriveFileId.message || "請輸入正確檔案 ID"}
                  </span>
                ) : null}
              </div>
            </div>

            <div className={baseStyles.infoRow}>
              <div className={`${baseStyles.infoRowLabel} ${styles.alignedLabel}`}>
                YouTube
              </div>
              <div className={baseStyles.infoRowBody}>
                <TextField
                  value={form.youtubeUrl}
                  width='100%'
                  fullWidth
                  placeholder='https://youtube.com/...'
                  onChange={(value) => setField("youtubeUrl", value)}
                  error={Boolean(errors.youtubeUrl)}
                />
                {errors.youtubeUrl ? (
                  <span className={styles.errorText}>
                    {errors.youtubeUrl.message || "請輸入正確網址"}
                  </span>
                ) : null}
              </div>
            </div>

            <div className={baseStyles.infoRow}>
              <div className={`${baseStyles.infoRowLabel} ${styles.alignedLabel}`}>
                Facebook
              </div>
              <div className={baseStyles.infoRowBody}>
                <TextField
                  value={form.facebookUrl}
                  width='100%'
                  fullWidth
                  placeholder='https://facebook.com/...'
                  onChange={(value) => setField("facebookUrl", value)}
                  error={Boolean(errors.facebookUrl)}
                />
                {errors.facebookUrl ? (
                  <span className={styles.errorText}>
                    {errors.facebookUrl.message || "請輸入正確網址"}
                  </span>
                ) : null}
              </div>
            </div>

            <div className={baseStyles.infoRow}>
              <div className={`${baseStyles.infoRowLabel} ${styles.alignedLabel}`}>
                Instagram
              </div>
              <div className={baseStyles.infoRowBody}>
                <TextField
                  value={form.instagramUrl}
                  width='100%'
                  fullWidth
                  placeholder='https://instagram.com/...'
                  onChange={(value) => setField("instagramUrl", value)}
                  error={Boolean(errors.instagramUrl)}
                />
                {errors.instagramUrl ? (
                  <span className={styles.errorText}>
                    {errors.instagramUrl.message || "請輸入正確網址"}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </form>

      <Alert
        isOpen={isConfirmLeaveOpen}
        onClose={() => setIsConfirmLeaveOpen(false)}
        onConfirm={handleLeaveConfirm}
        leftButtonText='留在此頁'
        rightButtonText='離開'
        mainTitle='尚未儲存，確定離開？'
        subTitle='離開後本次填寫的內容將不會保留。'
        isWarning
      />

      <Alert
        isOpen={isDeleteOpen}
        onClose={() => !isSubmitting && setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        leftButtonText='取消'
        rightButtonText={isSubmitting ? "刪除中…" : "刪除"}
        mainTitle='確認刪除此卡片？'
        subTitle='刪除後無法復原。'
        isWarning
      />

      <Toast
        show={toastState.show}
        title={toastState.title}
        subtitle={toastState.subtitle}
        error={toastState.error}
      />
    </RightPanelLayout>
  );
};

export default AiLearningResourceFormPage;
